import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _isEqual from 'lodash/isEqual';
import { isSameDay, format } from 'date-fns';

import LoaderBar from 'components/LoaderBar';
import DashboardContainer from 'components/DashboardContainer';

import TableTopFilters from 'components/TableTopFilters';
import SearchWithFilters from 'components/SearchWithFilters';
import { RANGES } from 'components/DateRangePicker/ranges';

import TransferTable from './TransferTable';

import { DATE_FOR_API } from 'utils/dateFormat';
import { usePrevious } from 'hooks';
import { transfersTable as TEXT } from 'locales/en/transfer';
import {
  transferAllSelector,
  setParams,
  getTransfers,
  setSearchTerm,
  searchTransfers,
  resetSearch,
} from 'redux/slices/transfer';

const Transfers = () => {
  const dispatch = useDispatch();
  const { data, params, pagination, isLoading, searchTerm } = useSelector(
    (state) => transferAllSelector(state)
  );
  const company = useSelector((state) => state.company.current);
  const prevCompany = usePrevious(company);
  const prevParams = usePrevious(params);
  const prevSearchTerm = usePrevious(searchTerm);

  useEffect(() => {
    dispatch(getTransfers());

    return () => {
      dispatch(resetSearch());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prevCompany !== null && prevCompany !== company) {
      dispatch(
        setParams({
          page: 1,
        })
      );
      dispatch(getTransfers());
    }
  }, [company, dispatch, prevCompany]);

  useEffect(() => {
    if (
      (prevParams !== null && !_isEqual(params, prevParams)) ||
      (!!prevSearchTerm && searchTerm === '')
    ) {
      dispatch(getTransfers());
    }
  }, [dispatch, params, prevParams, prevSearchTerm, searchTerm]);

  const onChangePerPage = (per_page) => {
    dispatch(
      setParams({
        per_page,
      })
    );
  };

  const onChangePage = ({ selected }) => {
    dispatch(
      setParams({
        page: selected + 1,
      })
    );
  };

  const onApplyDate = (date) => {
    dispatch(
      setParams({
        from: format(date.startDate, DATE_FOR_API),
        to: format(date.endDate, DATE_FOR_API),
      })
    );
  };

  const range = useMemo(() => {
    let id =
      RANGES.find(
        (item) =>
          isSameDay(new Date(item.range().startDate), new Date(params.from)) &&
          isSameDay(new Date(item.range().endDate), new Date(params.to))
      )?.range()?.id || RANGES[RANGES.length - 1]?.range()?.id;

    return {
      startDate: new Date(params.from),
      endDate: new Date(params.to),
      id,
      key: 'selection',
    };
  }, [params.from, params.to]);

  const onChangeSearchTerm = (event) => {
    dispatch(setSearchTerm(event.target.value || ''));
  };

  const onClearInput = () => {
    dispatch(resetSearch());
  };

  const onSearch = () => dispatch(searchTransfers());

  return (
    <DashboardContainer header={TEXT.header}>
      <LoaderBar isVisible={isLoading} />
      <SearchWithFilters
        onChangeInput={onChangeSearchTerm}
        searchTerm={searchTerm}
        onSearch={onSearch}
        placeholder={TEXT.search}
        onClearInput={onClearInput}
      />
      <TransferTable
        topContent={
          <TableTopFilters
            onApplyDate={onApplyDate}
            range={range}
            onConfirmExport={() => {}}
            isExportDisabled
          />
        }
        data={data}
        title={null}
        pagination={pagination}
        onClickPerPage={onChangePerPage}
        onPageChange={onChangePage}
      />
    </DashboardContainer>
  );
};

export default Transfers;
